import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import '../components/Signup/Signup.css';
import { useReceivers } from '../hooks/useApiHooks';
import { AddorEditReceiver } from '../components/ReceiverManagement/AddorEditReceiver';
import { DeleteReceiver } from '../components/ReceiverManagement/DeleteReceiver';
import { ReceiversTable } from '../components/ReceiverManagement/ReceiversTable';
import { ChevronDown, ArrowUpDown, Plus, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { useLanguage, getReceiverManagementTranslation } from '../hooks/useLanguage';
import { CardStat } from '../components/CardStat';

function ReceiverManagement() {
  const dispatch = useDispatch();
  const receivers = useSelector((state) => state.receivers || []);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSpanish] = useLanguage();

  useReceivers();

  const [formData, setFormData] = useState({
    nombre: '',
    nif: '',
    eori: '',
    direccion: '',
    localidad: '',
    pais: '',
  });
  const [isEditing, setIsEditing] = useState(false);
  const [currentReceiverId, setCurrentReceiverId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [receiverToDelete, setReceiverToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sortBy, setSortBy] = useState('name-asc');
  const [isSortOpen, setIsSortOpen] = useState(false);

  const sortOptions = [
    { value: 'name-asc', label: getReceiverManagementTranslation('nameAZ', isSpanish) },
    { value: 'name-desc', label: getReceiverManagementTranslation('nameZA', isSpanish) },
    { value: 'date-desc', label: getReceiverManagementTranslation('newestFirst', isSpanish) },
    { value: 'date-asc', label: getReceiverManagementTranslation('oldestFirst', isSpanish) }
  ];

  const filteredReceivers = receivers.filter(receiver => 
    receiver.nombre.toLowerCase().includes(searchQuery.toLowerCase()) ||
    receiver.nif?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    receiver.eori?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    receiver.pais?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedReceivers = [...filteredReceivers].sort((a, b) => {
    switch (sortBy) {
      case 'name-asc':
        return a.nombre.localeCompare(b.nombre);
      case 'name-desc':
        return b.nombre.localeCompare(a.nombre);
      case 'date-asc':
        return new Date(a.date_created) - new Date(b.date_created);
      case 'date-desc':
        return new Date(b.date_created) - new Date(a.date_created);
      default:
        return 0;
    }
  });

  const getSortLabel = () => {
    return sortOptions.find(option => option.value === sortBy)?.label || getReceiverManagementTranslation('sortBy', isSpanish);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAdd = () => {
    setIsEditing(false);
    setFormData({
      nombre: '',
      nif: '',
      eori: '',
      direccion: '',
      localidad: '',
      pais: '',
    });
    setIsModalOpen(true);
  };

  const handleEdit = (receiver) => {
    setIsEditing(true);
    setCurrentReceiverId(receiver.id);
    setFormData({
      nombre: receiver.nombre || '',
      nif: receiver.nif || '',
      eori: receiver.eori || '',
      direccion: receiver.direccion || '',
      localidad: receiver.localidad || '',
      pais: receiver.pais || '',
    });
    setIsModalOpen(true);
  };

  const openDeleteModal = (receiver) => {
    setReceiverToDelete(receiver);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setReceiverToDelete(null);
  };

  const confirmDelete = async () => {
    if (!receiverToDelete) return;

    try {
      setIsLoading(true);
      const response = await fetch(`https://${process.env.REACT_APP_DOCS_API_KEY}/api/items/receivers/${receiverToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete receiver');
      }

      dispatch({ type: 'DELETE_RECEIVER', payload: receiverToDelete.id });
      toast.success(getReceiverManagementTranslation('successDeleteReceiver', isSpanish));
      closeDeleteModal();

    } catch (err) {
      console.error('Error deleting receiver:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const apiData = {
      nombre: formData.nombre,
      nif: formData.nif,
      eori: formData.eori,
      direccion: formData.direccion,
      localidad: formData.localidad,
      pais: formData.pais,
    };

    try {
      let response;

      if (isEditing) {
        response = await fetch(`https://${process.env.REACT_APP_DOCS_API_KEY}/api/items/receivers/${currentReceiverId}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(apiData),
        });

        toast.success(getReceiverManagementTranslation('successUpdateReceiver', isSpanish));

      } else {
        response = await fetch(`https://${process.env.REACT_APP_DOCS_API_KEY}/api/items/receivers`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(apiData),
        });

        toast.success(getReceiverManagementTranslation('successCreateReceiver', isSpanish));

      }

      if (!response.ok) {
        throw new Error(`Failed to ${isEditing ? 'update' : 'create'} receiver`);
      }

      const data = await response.json();

      const receiverData = data.data || data;

      if (isEditing) {
        dispatch({ type: 'UPDATE_RECEIVER', payload: receiverData });
      } else {
        dispatch({ type: 'ADD_RECEIVER', payload: receiverData });
      }

      setIsModalOpen(false);
      setFormData({
        nombre: '',
        nif: '',
        eori: '',
        direccion: '',
        localidad: '',
        pais: '',
      });

      fetchReceivers();

    } catch (err) {
      console.error('Error submitting receiver:', err);
      if (isEditing) {
        toast.error(getReceiverManagementTranslation('errorUpdateReceiver', isSpanish));
      } else {
        toast.error(getReceiverManagementTranslation('errorCreateReceiver', isSpanish));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchReceivers = async () => {
    const userId = sessionStorage.getItem('user_id');
    if (!userId) {
      console.warn('No user_id found in sessionStorage, receiver fetch might fail');
      return;
    }

    try {
      const response = await fetch(
        `https://${process.env.REACT_APP_DOCS_API_KEY}/api/items/receivers?filter%5Buser_id%5D%5B_eq%5D=${userId}`,
        {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            'accept': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch receivers');
      }

      const data = await response.json();

      const receiversData = data.data || (Array.isArray(data) ? data : []);

      dispatch({ type: 'SET_RECEIVERS', payload: receiversData });
    } catch (err) {
      console.error("Error fetching receivers:", err.message);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Find the city (localidad) with the most receivers
  const cityCounts = receivers.reduce((acc, receiver) => {
    if (receiver.localidad) {
      acc[receiver.localidad] = (acc[receiver.localidad] || 0) + 1;
    }
    return acc;
  }, {});
  let topCity = '-';
  let topCityCount = 0;
  for (const [city, count] of Object.entries(cityCounts)) {
    if (count > topCityCount) {
      topCity = city;
      topCityCount = count;
    }
  }

  // Find the country (pais) with the most receivers
  const countryCounts = receivers.reduce((acc, receiver) => {
    if (receiver.pais) {
      acc[receiver.pais] = (acc[receiver.pais] || 0) + 1;
    }
    return acc;
  }, {});
  let topCountry = '-';
  let topCountryCount = 0;
  for (const [country, count] of Object.entries(countryCounts)) {
    if (count > topCountryCount) {
      topCountry = country;
      topCountryCount = count;
    }
  }

  return (
    <div className="text-left">
      <h1 className='text-3xl font-bold mb-20'>{getReceiverManagementTranslation('receiverInformationManagement', isSpanish)}</h1>
      <div className="grid grid-cols-[1fr_4fr_1fr] gap-4 mb-8 pb-8 border-bottom-gray">
        <CardStat value={receivers.length} title={isSpanish ? 'Receptores' : 'Receivers'} />
        <CardStat value={topCity} title={isSpanish ? 'Ciudad con más receptores' : 'Top Receiver City'} />
        <CardStat value={topCountry} title={isSpanish ? 'País con más receptores' : 'Top Receiver Country'} />
      </div>
      <div className='flex justify-between gap-4 mb-4 items-center'>
        <h2 className='text-xl font-bold'>{isSpanish ? 'Lista de receptores' : 'Receiver List'}</h2>
        <div className="flex gap-2 items-center">
          {/* Custom Sort Dropdown */}
          <div className="relative button-no-hover-effect">
            <button
              className="flex items-center gap-2 px-2 py-2 rounded-full truncate"
              onClick={() => setIsSortOpen(!isSortOpen)}
            >
              <ArrowUpDown className="w-4 h-4" />
              <span>{getSortLabel()}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${isSortOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {isSortOpen && (
              <div className="overflow-hidden absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border-container py-1 z-10">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                      sortBy === option.value ? 'text-primary' : 'text-gray-700'
                    }`}
                    onClick={() => {
                      setSortBy(option.value);
                      setIsSortOpen(false);
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Search Input */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="w-64 pl-10 pr-4 py-2 border-container rounded-full"
              placeholder={getReceiverManagementTranslation('searchReceivers', isSpanish)}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <button
            onClick={handleAdd}
            className='border rounded-full py-2 px-4 pr-6 truncate flex gap-2 items-center bg-default text-white'
            disabled={isLoading}
          >
            <Plus className="size-5 stroke-white" />
            {getReceiverManagementTranslation('addReceiver', isSpanish)}
          </button>
        </div>
      </div>

      {/* Modal for Adding/Editing Receiver */}
      {isModalOpen && (
        <AddorEditReceiver handleSubmit={handleSubmit} handleChange={handleChange} isEditing={isEditing} closeModal={closeModal}
          formData={formData} isLoading={isLoading} />
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && receiverToDelete && (
        <DeleteReceiver closeDeleteModal={closeDeleteModal} isLoading={isLoading} receiverToDelete={receiverToDelete} confirmDelete={confirmDelete} />
      )}

      <ReceiversTable receivers={sortedReceivers} isLoading={isLoading} handleEdit={handleEdit} openDeleteModal={openDeleteModal} />

    </div>
  );
}

export default ReceiverManagement;